import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { Banknote, CircleAlert, Pencil, Plus, RefreshCw, Save, Trash2, X } from "lucide-react";

import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard, SkeletonTable } from "@/components/skeleton";
import { apiFetch } from "@/lib/api-client";
import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

interface TaxRate {
  id: string;
  name: string;
  country: string;
  countryName: string;
  region: string | null;
  ratePercent: number;
  isDefault: boolean;
  isActive: boolean;
  effectiveFrom: string;
  updatedAt: string;
}

interface TaxRatePayload {
  name: string;
  country: string;
  region: string | null;
  ratePercent: number;
  isDefault: boolean;
}

const EMPTY_FORM: TaxRatePayload = {
  name: "",
  country: "",
  region: null,
  ratePercent: 0,
  isDefault: false,
};

function TaxRatesSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-44" />
        <Skeleton className="mt-2 h-4 w-96" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.2fr)]">
        <SkeletonCard className="h-[320px]" />
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <SkeletonTable rows={6} cols={5} />
        </div>
      </div>
    </div>
  );
}

function toPayload(rate: TaxRate): TaxRatePayload {
  return {
    name: rate.name,
    country: rate.country,
    region: rate.region,
    ratePercent: rate.ratePercent,
    isDefault: rate.isDefault,
  };
}

export default function TaxRatesPage() {
  const { t } = useTranslation();
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<TaxRatePayload>(EMPTY_FORM);
  const [editForm, setEditForm] = useState<TaxRatePayload>(EMPTY_FORM);

  const loadRates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: TaxRate[] }>("/platform/billing/tax-rates");
      setRates(response.data);
    } catch {
      setRates([]);
      setError(t(MSG.admin.taxRates.messages.unavailable));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadRates();
  }, [loadRates]);

  const editingRate = useMemo(
    () => rates.find((rate) => rate.id === editingId) ?? null,
    [editingId, rates],
  );

  const handleCreateChange = (field: keyof TaxRatePayload, value: string | number | boolean | null) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const handleEditChange = (field: keyof TaxRatePayload, value: string | number | boolean | null) => {
    setEditForm((current) => ({ ...current, [field]: value }));
  };

  const parseNumericRate = (value: string) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const resetEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      await apiFetch<{ data: TaxRate }>("/platform/billing/tax-rates", {
        method: "POST",
        body: JSON.stringify({
          ...createForm,
          country: createForm.country.trim().toUpperCase(),
          region: createForm.region?.trim() ? createForm.region.trim() : null,
        }),
      });
      setCreateForm(EMPTY_FORM);
      await loadRates();
    } catch {
      setError(t(MSG.admin.taxRates.messages.createError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingRate) {
      return;
    }

    setIsSaving(true);

    try {
      await apiFetch<{ data: TaxRate }>(`/platform/billing/tax-rates/${editingRate.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...editForm,
          country: editForm.country.trim().toUpperCase(),
          region: editForm.region?.trim() ? editForm.region.trim() : null,
        }),
      });
      resetEdit();
      await loadRates();
    } catch {
      setError(t(MSG.admin.taxRates.messages.saveError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (taxRateId: string) => {
    setIsDeletingId(taxRateId);

    try {
      await apiFetch<{ success: boolean }>(`/platform/billing/tax-rates/${taxRateId}`, {
        method: "DELETE",
      });
      if (editingId === taxRateId) {
        resetEdit();
      }
      await loadRates();
    } catch {
      setError(t(MSG.admin.taxRates.messages.deleteError));
    } finally {
      setIsDeletingId(null);
    }
  };

  if (isLoading) {
    return <TaxRatesSkeleton />;
  }

  if (error && rates.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{t(MSG.admin.taxRates.title)}</h1>
          <p className="mt-1 text-muted-foreground">{t(MSG.admin.taxRates.description)}</p>
        </div>
        <EmptyState
          icon={<CircleAlert className="h-8 w-8" />}
          title={t(MSG.admin.taxRates.unavailableTitle)}
          description={t(MSG.admin.taxRates.unavailableDescription)}
          actionLabel={t(MSG.admin.taxRates.retry)}
          onAction={() => void loadRates()}
          variant="info"
          tier={2}
          eyebrow="Platform Billing"
          checklistItems={[
            t(MSG.admin.taxRates.checklist.routeReachable),
            t(MSG.admin.taxRates.checklist.tableMigrated),
            t(MSG.admin.taxRates.checklist.retryHealthy),
          ]}
          supportingContent={(
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {t(MSG.admin.taxRates.supportingContent)}
            </p>
          )}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <Banknote className="h-5 w-5 text-[var(--color-primary)]" />
            <h1 className="text-2xl font-bold text-white">{t(MSG.admin.taxRates.title)}</h1>
          </div>
          <p className="mt-1 text-muted-foreground">
            {t(MSG.admin.taxRates.description)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadRates()}
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-white transition hover:bg-[var(--color-surface)]"
        >
          <RefreshCw className="h-4 w-4" />
          {t(MSG.admin.taxRates.refresh)}
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {error}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.2fr)]">
        <form
          onSubmit={(event) => void handleCreate(event)}
          className="space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-5"
        >
          <div>
            <h2 className="text-lg font-semibold text-white">Add tax rate</h2>
            <p className="mt-1 text-sm text-muted-foreground">Use the mounted admin billing seam to add a new active tax rule.</p>
          </div>

          <label className="block space-y-2">
            <span className="text-sm text-muted-foreground">Name</span>
            <input
              value={createForm.name}
              onChange={(event) => handleCreateChange("name", event.target.value)}
              placeholder="Romania VAT"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm text-muted-foreground">Country</span>
              <input
                value={createForm.country}
                onChange={(event) => handleCreateChange("country", event.target.value.toUpperCase())}
                placeholder="RO"
                maxLength={2}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm uppercase text-white"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm text-muted-foreground">Region</span>
              <input
                value={createForm.region ?? ""}
                onChange={(event) => handleCreateChange("region", event.target.value)}
                placeholder="Optional"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
              />
            </label>
          </div>

          <label className="block space-y-2">
            <span className="text-sm text-muted-foreground">Rate percent</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={createForm.ratePercent}
              onChange={(event) => handleCreateChange("ratePercent", parseNumericRate(event.target.value))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
            />
          </label>

          <label className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white">
            <input
              type="checkbox"
              checked={createForm.isDefault}
              onChange={(event) => handleCreateChange("isDefault", event.target.checked)}
            />
            Make this the default active rate for the country/region pair
          </label>

          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            Create tax rate
          </button>
        </form>

        <div className="space-y-4">
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-background)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Rate</th>
                  <th className="px-4 py-3">Default</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {rates.map((rate) => {
                  const isEditing = editingId === rate.id;
                  const currentForm = isEditing ? editForm : toPayload(rate);

                  return (
                    <tr key={rate.id} className="stagger-item">
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <div className="space-y-2">
                            <input
                              value={currentForm.name}
                              onChange={(event) => handleEditChange("name", event.target.value)}
                              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
                            />
                            <input
                              value={currentForm.region ?? ""}
                              onChange={(event) => handleEditChange("region", event.target.value)}
                              placeholder="Region"
                              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
                            />
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-white">{rate.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {rate.countryName}{rate.region ? ` · ${rate.region}` : ""}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <input
                            value={currentForm.country}
                            onChange={(event) => handleEditChange("country", event.target.value.toUpperCase())}
                            maxLength={2}
                            className="w-20 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm uppercase text-white"
                          />
                        ) : (
                          <span className="font-mono text-white">{rate.country}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={currentForm.ratePercent}
                            onChange={(event) => handleEditChange("ratePercent", parseNumericRate(event.target.value))}
                            className="w-24 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
                          />
                        ) : (
                          <span className="text-white">{rate.ratePercent.toFixed(2)}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <label className="inline-flex items-center gap-2 text-sm text-white">
                            <input
                              type="checkbox"
                              checked={currentForm.isDefault}
                              onChange={(event) => handleEditChange("isDefault", event.target.checked)}
                            />
                            Default
                          </label>
                        ) : rate.isDefault ? (
                          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-300">
                            Default
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                onClick={() => void handleSaveEdit()}
                                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs text-white transition hover:bg-[var(--color-surface)]"
                              >
                                <Save className="h-3.5 w-3.5" />
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={resetEdit}
                                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs text-white transition hover:bg-[var(--color-surface)]"
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(rate.id);
                                  setEditForm(toPayload(rate));
                                }}
                                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs text-white transition hover:bg-[var(--color-surface)]"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDelete(rate.id)}
                                disabled={isDeletingId === rate.id}
                                className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 px-3 py-2 text-xs text-rose-200 transition hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">Accounting follow-through</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Annual tax summary and CSV export remain intentionally unavailable here until the later accounting/reporting slice mounts a truthful backend contract. This page now closes only the tax-rate management requirement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
